import {useEffect, useState} from 'react';
import {
    Box,
    Paper,
    TableContainer,
    Table,
    TableBody,
    TableRow,
    TableCell,
    TablePagination,
    Typography,
    ListItem,
    CircularProgress,
    TableHead,
    Chip,
    IconButton,
    Tooltip,
    Avatar,
    AvatarGroup,
    Badge,
    Popover,
    Stack,
} from '@mui/material';

import {useDispatch, useSelector} from 'react-redux';
import {blockContact, setSavedContactDetails} from '../../actions/contacts/contacts';
import {getListContactsAdvanced} from '../../actions/contacts/contactsAdvanced';
import {useToken} from '../../hooks/useToken';
import {useNavigate} from 'react-router-dom';
import {useAppInsightsContext} from "@microsoft/applicationinsights-react-js";
import {SeverityLevel} from '@microsoft/applicationinsights-web';
import {
    getContactMessagesByIdContact,
    messagesSetSelectContactMessage,
    messagesLoading,
} from '../../actions/messages';
import {setSelectedContact} from '../../actions/contacts/contacts';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChatIcon from '@mui/icons-material/Chat';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import {formatToLocalTimezone} from '../../utils/dateUtils';

const CATEGORY_AVATAR_SIZE = 28;

// ─── Helpers visuales ───────────────────────────────────────────────────────

/**
 * Genera las iniciales del contacto para mostrar en el Avatar
 * cuando no hay foto de perfil disponible.
 */
const getInitials = (firstName, lastName) => {
    const first = firstName?.[0] ?? '';
    const last = lastName?.[0] ?? '';
    return (first + last).toUpperCase() || '?';
};

/**
 * Genera un color de fondo determinista para el Avatar
 * basado en el nombre del contacto.
 */
const stringToColor = (str = '') => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const h = Math.abs(hash) % 360;
    return `hsl(${h}, 45%, 52%)`;
};

// ─── CategoryAvatarGroup ─────────────────────────────────────────────────────

const CategoryAvatarGroup = ({categories}) => {
    const [anchor, setAnchor] = useState(null);

    if (!categories?.length) {
        return (
            <Typography variant="body2" color="text.disabled">
                Sin categoría
            </Typography>
        );
    }

    return (
        <>
            <AvatarGroup
                max={3}
                onClick={(e) => setAnchor(e.currentTarget)}
                sx={{
                    cursor: 'pointer',
                    justifyContent: 'center',
                    '& .MuiAvatar-root': {
                        width: CATEGORY_AVATAR_SIZE,
                        height: CATEGORY_AVATAR_SIZE,
                        fontSize: 12,
                    },
                }}
            >
                {categories.map((cat) => (
                    <Tooltip key={cat.id} title={cat.name}>
                        <Avatar sx={{width: 28, height: 28, fontSize: '0.7rem', fontWeight: 700, bgcolor: cat.color}}>
                            {cat.name?.[0]?.toUpperCase() ?? '?'}
                        </Avatar>
                    </Tooltip>
                ))}
            </AvatarGroup>
            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                transformOrigin={{vertical: 'top', horizontal: 'center'}}
                PaperProps={{sx: {p: 1.5, maxWidth: 280}}}
            >
                <Stack direction="row" flexWrap="wrap" gap={0.75}>
                    {categories.map((cat) => (
                        <Chip
                            key={cat.id}
                            label={cat.name}
                            size="small"
                            sx={{
                                backgroundColor: cat.color,
                                color: '#fff',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                borderRadius: '6px',
                            }}
                        />
                    ))}
                </Stack>
            </Popover>
        </>
    );
};

// ─── Estilos compartidos ─────────────────────────────────────────────────────

const headCellSx = {
    fontWeight: 600,
    color: 'text.secondary',
    fontSize: '0.8rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    borderBottom: '2px solid',
    borderColor: 'divider',
};

// ─── Componente principal ────────────────────────────────────────────────────

const TableContactsBody = ({
                               contacts,
                               loading,
                               count,
                               id_customer,
                               agents = [],
                               appliedFilters = {},
                           }) => {
    const [dense] = useState(false);
    const [selected] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [showLgUp] = useState(true);
    const [paginating, setPaginating] = useState(false);

    const appInsights = useAppInsightsContext();
    const {contactsMessages} = useSelector((state) => state.messages);
    const agent = useSelector((state) => state.agent);
    const {getTokenSilent} = useToken();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        appInsights.trackTrace({
            message: 'render de tabla de contactos',
            properties: {totalContacts: contacts.length, page, rowsPerPage},
            severityLevel: SeverityLevel.Information,
        });
    }, [appInsights, contacts.length, page, rowsPerPage]);

    // Resetear página cuando cambia el total (nuevo filtro aplicado)
    useEffect(() => {
        setPage(0);
    }, [count]);

    // Cuando llegan los contactos nuevos, la paginación terminó
    useEffect(() => {
        if (paginating) setPaginating(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contacts]);

    const isSelected = (id) => selected.indexOf(id) !== -1;

    const handleClick = (event, item) => {
        dispatch(setSelectedContact(item));
        navigate(`/admin/contacts/${item._id}`);
    };

    const handleChangePage = (event, newPage) => {
        if (newPage < 0) return;
        setPage(newPage);
        setPaginating(true);
        getTokenSilent().then((token) => {
            dispatch(
                getListContactsAdvanced(
                    id_customer,
                    token,
                    appliedFilters,
                    newPage * rowsPerPage,
                    rowsPerPage
                )
            );
        });
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleViewChat = (contact) => {
        dispatch(setSelectedContact(contact));
        const contactMessage = contactsMessages.find(
            (msg) => msg.id_contact === contact._id
        );

        if (contactMessage) {
            dispatch(messagesSetSelectContactMessage(contactMessage));
        } else {
            dispatch(messagesLoading(true));
            getTokenSilent().then((accessToken) => {
                dispatch(
                    getContactMessagesByIdContact(
                        contact._id,
                        accessToken,
                        null,
                        contact.unread_message
                    )
                );
            });
        }

        dispatch(setSavedContactDetails(contact));
        navigate('/admin/chat');
    };

    const handleBlockContact = (agent, contact) => {
        const updatedContact = {...contact, blocked: !contact.blocked};
        dispatch({type: 'UPDATE_CONTACT_ADVANCED', payload: updatedContact});
        getTokenSilent().then((accessToken) => {
            dispatch(blockContact(agent._id, agent.name, contact.phone, accessToken));
        });
    };

    const formatMessageUpdate = (messageUpdate) => {
        if (!messageUpdate) return 'Sin interacción';
        const formattedDate = formatToLocalTimezone(
            messageUpdate,
            {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true,
            },
            'en-US'
        );
        return formattedDate === 'Fecha inválida' ? 'Sin interacción' : formattedDate;
    };

    const getAgentName = (id_agent) => {
        if (!id_agent) return 'Sin agente';
        const found = agents.find((a) => a.id === id_agent);
        return found ? found.name : 'Agente desconocido';
    };

    const getNoContactsMessage = () => {
        const hasDateFilter = appliedFilters.date_from || appliedFilters.date_to;
        const hasAgentFilter = appliedFilters.id_agent;
        const hasTypeFilter =
            appliedFilters.agent_filter_type &&
            appliedFilters.agent_filter_type !== 'all';

        let message = 'No se encontraron contactos';

        if (hasAgentFilter) {
            const selectedAgent = agents.find((a) => a.id === appliedFilters.id_agent);
            message = `${selectedAgent?.name ?? 'El agente seleccionado'} no tiene contactos`;
        } else if (hasTypeFilter) {
            if (appliedFilters.agent_filter_type === 'assigned')
                message = 'No se encontraron contactos asignados a agentes';
            else if (appliedFilters.agent_filter_type === 'unassigned')
                message = 'No se encontraron contactos sin asignar';
        }

        if (hasDateFilter) {
            const dateInfo = [];
            if (appliedFilters.date_from)
                dateInfo.push(`desde ${new Date(appliedFilters.date_from).toLocaleDateString()}`);
            if (appliedFilters.date_to)
                dateInfo.push(`hasta ${new Date(appliedFilters.date_to).toLocaleDateString()}`);
            const prefix = hasAgentFilter || hasTypeFilter ? 'en el período' : 'en el rango de fechas';
            message += ` ${prefix} ${dateInfo.join(' ')}`;
        }

        if (!hasDateFilter && !hasAgentFilter && !hasTypeFilter)
            message = 'No se encontraron contactos con los filtros aplicados';

        return message;
    };

    // ─── Render ──────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <ListItem sx={{display: 'flex', justifyContent: 'center', p: 20, m: 1}}>
                <CircularProgress/>
            </ListItem>
        );
    }

    if (!showLgUp) return null;

    return (
        <Box sx={{width: '100%', display: 'flex', justifyContent: 'center'}}>
            <Paper sx={{width: '100%', maxWidth: 1100, mb: 2, overflow: 'hidden', borderRadius: 2}}>
                <Box sx={{ position: 'relative' }}>
                {paginating && (
                    <Box sx={{
                        position: 'absolute', inset: 0, zIndex: 2,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: 'rgba(255,255,255,0.55)',
                        borderRadius: 2,
                    }}>
                        <CircularProgress size={32} />
                    </Box>
                )}
                <TableContainer>
                    <Table
                        sx={{minWidth: 1000, opacity: paginating ? 0.45 : 1, transition: 'opacity 0.2s'}}
                        aria-labelledby="tableTitle"
                        size={dense ? 'small' : 'medium'}
                    >
                        {/* ── Encabezado ── */}
                        <TableHead>
                            <TableRow sx={{backgroundColor: 'background.default'}}>
                                <TableCell align="left" sx={headCellSx}>Nombre</TableCell>
                                <TableCell align="center" sx={headCellSx}>Categoría</TableCell>
                                <TableCell align="center" sx={headCellSx}>Última interacción</TableCell>
                                <TableCell align="center" sx={headCellSx}>Agente</TableCell>
                                {/* Columna de acciones sin label */}
                                <TableCell align="center" sx={{...headCellSx, width: 140}}/>
                            </TableRow>
                        </TableHead>

                        {/* ── Cuerpo ── */}
                        <TableBody>
                            {/* Estado vacío */}
                            {count === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{py: 6}}>
                                        <Typography variant="body1" color="text.secondary">
                                            {getNoContactsMessage()}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}

                            {contacts.map((row) => {
                                const isItemSelected = isSelected(row._id);
                                const fullName = row.first_name
                                    ? `${row.first_name} ${row.last_name ?? ''}`.trim()
                                    : 'Sin nombre';
                                const avatarColor = stringToColor(fullName);

                                return (
                                    <TableRow
                                        key={row._id}
                                        aria-checked={isItemSelected}
                                        tabIndex={-1}
                                        selected={isItemSelected}
                                        hover
                                        sx={{
                                            '&:last-child td': {borderBottom: 0},
                                            transition: 'background-color 0.15s',
                                        }}
                                    >
                                        {/* ── Nombre + Avatar ── */}
                                        <TableCell align="left">
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                {/*
                                                     * Badge muestra un ícono verde si el contacto
                                                     * NO está bloqueado (equivale a "verificado / activo").
                                                     * Se oculta con invisible={row.blocked} para contactos bloqueados.
                                                     */}
                                                <Badge
                                                    overlap="circular"
                                                    anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                                                    invisible={row.blocked}
                                                    badgeContent={
                                                        <CheckCircleIcon
                                                            sx={{
                                                                fontSize: 14,
                                                                color: 'success.main',
                                                                backgroundColor: 'background.paper',
                                                                borderRadius: '50%',
                                                            }}
                                                        />
                                                    }
                                                >
                                                    <Avatar
                                                        sx={{
                                                            width: 40,
                                                            height: 40,
                                                            bgcolor: avatarColor,
                                                            fontSize: '0.875rem',
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {getInitials(row.first_name, row.last_name)}
                                                    </Avatar>
                                                </Badge>

                                                {/* Nombre y teléfono apilados */}
                                                <Stack spacing={0}>
                                                    <Typography variant="body1" fontWeight={600} lineHeight={1.2}>
                                                        {fullName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {row.phone}
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                        </TableCell>

                                        {/* ── Categorías ── */}
                                        <TableCell align="center">
                                            <CategoryAvatarGroup categories={row.categories}/>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2" color="text.secondary">
                                                {formatMessageUpdate(row.message_update)}
                                            </Typography>
                                        </TableCell>

                                        {/* ── Agente ── */}
                                        <TableCell align="center">
                                            <Typography variant="body2" color="text.secondary">
                                                {getAgentName(row.id_agent)}
                                            </Typography>
                                        </TableCell>

                                        {/* ── Acciones (3 botones en una sola celda) ── */}
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={0.5} justifyContent="center">
                                                <Tooltip title="Ver Perfil">
                                                    <IconButton
                                                        size="small"
                                                        color="info"
                                                        onClick={(e) => handleClick(e, row)}
                                                    >
                                                        <AccountCircleIcon fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title="Ver Chat">
                                                    <IconButton
                                                        size="small"
                                                        color="success"
                                                        onClick={() => handleViewChat(row)}
                                                    >
                                                        <ChatIcon fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip
                                                    title={
                                                        row.blocked
                                                            ? 'Desbloquear Contacto'
                                                            : 'Bloquear Contacto'
                                                    }
                                                >
                                                    <IconButton
                                                        size="small"
                                                        color={row.blocked ? 'success' : 'error'}
                                                        onClick={() => handleBlockContact(agent, row)}
                                                    >
                                                        {row.blocked ? (
                                                            <CheckCircleIcon fontSize="small"/>
                                                        ) : (
                                                            <BlockIcon fontSize="small"/>
                                                        )}
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                </Box>

                <TablePagination
                    rowsPerPageOptions={[5]}
                    component="div"
                    count={count}
                    rowsPerPage={rowsPerPage}
                    page={Math.max(0, page)}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    nextIconButtonProps={{ disabled: paginating }}
                    backIconButtonProps={{ disabled: paginating }}
                />
            </Paper>
        </Box>
    );
};

export default TableContactsBody;